import { useCallback, useEffect } from 'react';
import { Provider } from 'react-redux';
import { createStackNavigator } from '@react-navigation/stack';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useTranslation } from 'react-i18next';
import { View } from 'native-base';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import Constants from 'expo-constants';

import * as SplashScreen from 'expo-splash-screen';
import {
  useFonts,
  Inter_100Thin,
  Inter_200ExtraLight,
  Inter_300Light,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  Inter_800ExtraBold,
  Inter_900Black,
} from '@expo-google-fonts/inter';

import { RootNavigationParams } from '@app/navigations/root';
import { AuthTokenProvider } from '@app/features/app';
import { MainScreen } from '@app/screens/main';
import { LauncherScreen, LoginScreen, RecoverPasswordScreen, ResetPasswordScreen } from '@app/screens/access';

import { AppLocaleProvider } from '@pimm/common';
import { ThemeContainer } from '@app/theme';
import { translateSystem } from '@app/localization/system';
import { Header } from '@app/components/core';
import { DashboardProvider } from '@app/features/dashboard';
import { SiteConfigProvider } from '@app/features/store-core';
import { ServiceWorkerProvider } from '@app/context';
import { useAppDefaults } from '@app/features/app/hooks';

import store from './src/store/store';
const persistor = persistStore(store);
const RootNavigation = createStackNavigator<RootNavigationParams>();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

SplashScreen.preventAutoHideAsync();

const App = () => {
  const { i18n } = useTranslation();
  const [app] = useAppDefaults();
  const appName = Constants.expoConfig?.extra?.name;

  // TODO: need to revisit
  const [fontsLoaded, fontError] = useFonts({
    Inter_100Thin,
    Inter_200ExtraLight,
    Inter_300Light,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    Inter_800ExtraBold,
    Inter_900Black,
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded) await SplashScreen.hideAsync();
  }, [fontsLoaded]);

  useEffect(() => {
    if (app?.culture) {
      i18n.changeLanguage(app.culture);
      translateSystem(app.culture);
    }
  }, []);

  if (!fontsLoaded && !fontError) return null;

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AuthTokenProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeContainer>
              <View onLayout={onLayoutRootView} />
              <ServiceWorkerProvider>
                <AppLocaleProvider defaultCulture={app?.culture}>
                  <DashboardProvider>
                    <SiteConfigProvider>
                      <RootNavigation.Navigator initialRouteName="Main">
                        <RootNavigation.Screen
                          name="Launcher"
                          component={LauncherScreen}
                          options={{ headerShown: false, title: appName }}
                        />
                        <RootNavigation.Screen name="Login" component={LoginScreen} options={{ headerShown: false, title: appName }} />
                        <RootNavigation.Screen
                          name="RecoverPassword"
                          component={RecoverPasswordScreen}
                          options={{ headerShown: false, title: appName }}
                        />
                        <RootNavigation.Screen
                          name="ResetPassword"
                          component={ResetPasswordScreen}
                          options={{ headerShown: false, title: appName }}
                        />
                        <RootNavigation.Screen
                          name="Main"
                          component={MainScreen}
                          options={{ header: stackHeaderProps => <Header {...stackHeaderProps} />, title: appName }}
                        />
                      </RootNavigation.Navigator>
                    </SiteConfigProvider>
                  </DashboardProvider>
                </AppLocaleProvider>
              </ServiceWorkerProvider>
            </ThemeContainer>
          </QueryClientProvider>
        </AuthTokenProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
